"use client";
import { useState, useEffect } from "react";
import { getActiveProductSummaryFacetsFromAnswers } from "@/lib/productSummary/getActiveProductSummaryFacetsFromAnswers";
import { QuizOption, QuizConfig } from "../type";

export interface UseDynamicOptionsProps {
  filterCategory?: string;
  currentAnswers: Record<string, string | string[]>;
  searchableKeys: Set<string>; // Keys that have searchFilterKey in quiz config
  staticOptions?: QuizOption[]; // Static options to filter
  quizConfig: QuizConfig;
}

export const useDynamicOptions = ({
  filterCategory,
  currentAnswers,
  searchableKeys,
  staticOptions,
  quizConfig,
}: UseDynamicOptionsProps) => {
  const [options, setOptions] = useState<QuizOption[]>([]);
  const [availableValues, setAvailableValues] = useState<Set<string>>(
    new Set(),
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // If no filterCategory, return empty options immediately
    if (!filterCategory) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    const fetchDynamicOptions = async () => {
      setIsLoading(true);

      try {
        const additionalFacets: string[] = [];
        if (staticOptions) {
          staticOptions.forEach((option) => {
            if (option.facetMappings) {
              Object.keys(option.facetMappings).forEach((facetKey) => {
                additionalFacets.push(facetKey);
              });
            }
          });
        }

        const { facets, allFacets } =
          await getActiveProductSummaryFacetsFromAnswers({
            filterCategory,
            currentAnswers,
            searchableKeys,
            additionalFacets,
            quizConfig,
          });

        // Store available values for filtering
        const availableSet = new Set(Object.keys(facets));
        setAvailableValues(availableSet);

        // If static options are provided, filter them by available values
        if (staticOptions && staticOptions.length > 0) {
          const filteredStatic = staticOptions.filter((option) => {
            // If option has facetMappings, check if all mapped facets have results
            if (option.facetMappings) {
              return Object.entries(option.facetMappings).every(
                ([facetKey, facetValue]) => {
                  const facetData = allFacets[facetKey] || {};
                  return facetValue in facetData;
                },
              );
            }

            // Default behavior: check if option value exists in current filter
            const valueToCheck =
              option.searchFilterValue || option.value || option.label;
            return availableSet.has(valueToCheck);
          });
          setOptions(filteredStatic);
        } else {
          // No static options, use dynamic options
          // Sort by count (descending) and convert to options
          const sortedOptions = Object.entries(facets)
            .sort(([, countA], [, countB]) => countB - countA)
            .map(([value]) => ({
              label: value,
              searchFilterValue: value,
              value: value,
            }));
          setOptions(sortedOptions);
        }
      } catch (error) {
        console.error("Error fetching dynamic options:", error);
        setOptions([]);
        setAvailableValues(new Set());
      } finally {
        setIsLoading(false);
      }
    };

    fetchDynamicOptions();
  }, [
    filterCategory,
    currentAnswers,
    searchableKeys,
    staticOptions,
    quizConfig,
  ]);

  return {
    options,
    availableValues,
    isLoading,
  };
};
